import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { RiDeleteBin6Line, RiShoppingCartLine, RiArrowRightUpLine } from 'react-icons/ri';
import { AiOutlinePlus, AiOutlineMinus } from 'react-icons/ai';
import './Cart.css';
import { useCart } from '../../context/CartContext.js';
import { usePublicSettings } from '../../context/PublicSettingsContext';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import translationMenu from '../../data/translationMenu';
import AuthContext from '../../context/AuthContext';
import CustomModal from '../../components/UI/CustomModal/customModal';
import Button from '../../components/UI/Button/Button';
import LanguageContext from '../../context/LanguageContext.js';
import { getVariantSizeDisplay } from '../../utils/variantDisplay';
import { computeOrderBreakdown, formatTaxRateForLabel } from '../../utils/orderPricing';

const Cart = () => {
    const { dir } = useContext(LanguageContext);
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;
    const { cartItems, removeFromCart, updateCartItemQuantity, cartRemainingTime, formatTime } = useCart();
    const { formatPrice, publicSettings } = usePublicSettings();
    const { user } = useContext(AuthContext);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const navigate = useNavigate();
    const showTrackOrdersLinks = (process.env.REACT_APP_SHOW_TRACK_ORDERS_LINKS || 'false').toLowerCase() === 'true';
    const layoutDir = dir();

    const subtotalAmount = cartItems.reduce(
        (total, item) => total + Number(item.price) * Number(item.quantity), 0
    );

    const { tax: cartTax, totalAmount: cartTotal } = computeOrderBreakdown({
        subtotal: subtotalAmount,
        orderType: 'pickup',
        discount: { code: 'None', discountAmount: 0, discountPercentage: 0 },
        defaultDeliveryFee: publicSettings?.defaultDeliveryFee ?? 0,
        taxRatePercent: publicSettings?.taxRatePercent ?? 0,
        pricesIncludeTax: Boolean(publicSettings?.pricesIncludeTax)
    });

    const cartTaxLabel = t(publicSettings?.pricesIncludeTax ? 'cart.taxIncludedRate' : 'cart.taxRate', {
        rate: formatTaxRateForLabel(publicSettings?.taxRatePercent ?? 0)
    });

    const handleCheckout = () => {
        if (!user._id) {
            setModalIsOpen(true);
        } else {
            navigate(`/${currentLanguage}/checkout`);
        }
    };

    return (
        <main className="main">
            <Breadcrumb title={t('cart.title')} />

            <section className="cart-page">
                <div className="container cart-body" dir={layoutDir}>
                    {cartItems.length === 0 ? (
                        <div className="cart-empty">
                            <div className="cart-empty-visual" aria-hidden="true">
                                <RiShoppingCartLine className="cart-empty-icon" />
                            </div>
                            <h2 className="cart-empty-title">{t('cart.empty')}</h2>
                            <p className="cart-empty-text">{t('cart.emptyDescription')}</p>
                            <Link to={`/${currentLanguage}/menu`} className="cart-btn cart-btn--primary">
                                <span>{t('cart.browseMenu')}</span>
                                <RiArrowRightUpLine className="cart-btn-ico" aria-hidden />
                                <span className="cart-btn-shine" aria-hidden />
                            </Link>
                        </div>
                    ) : (
                        <div className="cart-layout">
                            <div>
                                <p className="cart-line-summary" role="status">
                                    {t('cart.summary', { count: cartItems.length })}
                                </p>
                                <ul className="cart-list">
                                    {cartItems.map((item) => {
                                        const sizeLabel = getVariantSizeDisplay(item.selectedVariant?.size, currentLanguage);
                                        return (
                                        <li key={item.id}>
                                            <article className="cart-card">
                                                <div className="cart-card-media">
                                                    <img
                                                        src={item.imageUrl}
                                                        alt={
                                                            item.name?.[currentLanguage] ||
                                                            item.name?.en ||
                                                            t('cart.itemFallback')
                                                        }
                                                        className="cart-card-img"
                                                    />
                                                </div>
                                                <div>
                                                    <h2 className="cart-card-title">
                                                        {item.name?.[currentLanguage] || item.name?.en || t('cart.unknownItem')}
                                                        {sizeLabel ? (
                                                            <span className="cart-card-variant">({sizeLabel})</span>
                                                        ) : null}
                                                    </h2>
                                                    <p className="cart-card-category">
                                                        {translationMenu[currentLanguage]?.categories?.[item.category] ||
                                                            item.category}
                                                    </p>
                                                    <p className="cart-card-unit">{formatPrice(item.price)}</p>
                                                </div>
                                                <div className="cart-card-qty" role="group" aria-label={t('cart.title')}>
                                                    <button
                                                        type="button"
                                                        className="cart-qty-btn"
                                                        onClick={() => updateCartItemQuantity(item.id, item.quantity - 1)}
                                                        aria-label={t('cart.decreaseQuantity')}
                                                    >
                                                        <AiOutlineMinus aria-hidden />
                                                    </button>
                                                    <span className="cart-qty-value">{item.quantity}</span>
                                                    <button
                                                        type="button"
                                                        className="cart-qty-btn"
                                                        onClick={() => updateCartItemQuantity(item.id, item.quantity + 1)}
                                                        aria-label={t('cart.increaseQuantity')}
                                                    >
                                                        <AiOutlinePlus aria-hidden />
                                                    </button>
                                                </div>
                                                <div className="cart-card-total">
                                                    <span className="cart-card-line-total">
                                                        {formatPrice(item.price * item.quantity)}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className="cart-card-remove"
                                                        onClick={() => removeFromCart(item.id)}
                                                        aria-label={t('cart.remove')}
                                                    >
                                                        <RiDeleteBin6Line aria-hidden />
                                                    </button>
                                                </div>
                                            </article>
                                        </li>
                                        );
                                    })}
                                </ul>
                            </div>

                            <aside className="cart-summary-panel">
                                <h2 className="cart-summary-heading">{t('cart.orderSummary')}</h2>
                                <div className="cart-summary-rows">
                                    <div className="cart-summary-row">
                                        <span>{t('cart.subtotal')}</span>
                                        <span>{formatPrice(subtotalAmount)}</span>
                                    </div>
                                    <div className="cart-summary-row">
                                        <span>{cartTaxLabel}</span>
                                        <span>{formatPrice(cartTax)}</span>
                                    </div>
                                    <div className="cart-summary-row cart-summary-row--total">
                                        <span>{t('cart.total')}</span>
                                        <span>{formatPrice(cartTotal)}</span>
                                    </div>
                                </div>

                                {cartRemainingTime > 0 && (
                                    <div className="cart-expiry-notice" role="status">
                                        {t('cart.expiresIn')} {formatTime(cartRemainingTime)}
                                    </div>
                                )}

                                <button
                                    type="button"
                                    className="cart-btn cart-btn--primary cart-btn--block"
                                    onClick={handleCheckout}
                                >
                                    <span>{t('cart.proceedToCheckout')}</span>
                                    <RiArrowRightUpLine className="cart-btn-ico" aria-hidden />
                                    <span className="cart-btn-shine" aria-hidden />
                                </button>

                                <div className="cart-summary-foot">
                                    {showTrackOrdersLinks && (
                                        <Link to={`/${currentLanguage}/track-order`} className="cart-foot-link">
                                            {t('cart.trackOrders')}
                                        </Link>
                                    )}
                                    <Link to={`/${currentLanguage}/menu`} className="cart-foot-link cart-foot-link--strong">
                                        {t('cart.continueShopping')}
                                    </Link>
                                </div>
                            </aside>
                        </div>
                    )}
                </div>
            </section>

            <CustomModal setIsModalOpen={setModalIsOpen} isModalOpen={modalIsOpen} type="confirm">
                <div className="modal-header">
                    <h3>{t('cart.loginRequired')}</h3>
                </div>
                <div className="modal-body">
                    <p>{t('cart.loginRequired')}</p>
                </div>
                <div className="modal-footer">
                    <Button variant="outline" onClick={() => setModalIsOpen(false)}>
                        {t('myOrders.cancelOrder')}
                    </Button>
                    <Button
                        flex
                        onClick={() => {
                            setModalIsOpen(false);
                            navigate(`/${currentLanguage}/sign-in`);
                        }}
                    >
                        {t('nav.login')}
                    </Button>
                </div>
            </CustomModal>
        </main>
    );
};

export default Cart;
